import { Grid, Typography, Box, Button, IconButton } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import * as React from 'react';
import { RHFMultipleSelectField, RHFSelectField, RHFSwitch, RHFTextField } from "../../common/hook-form";
import { useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import { COURIER_LIST_QUERY_PAYLOAD } from "../../common/ProshipFormComponents";
import { apiConstants, getGeneric } from "../../common/constants";

/**
 * Shared "Calling Filter" UI + payload helpers for the NDR calling partner config pages
 * (Futworks / Growup Next / Futwork AI). The backend field is `calling_filter` on the partner
 * config; all criteria are ANDed and an empty criterion means "no restriction".
 *
 * The State / Pincode criterion is a list of rows; each row says whether matching shipments are
 * called and, for partners with projects (Futwork, GrowupNext), optionally in which project
 * (e.g. a Telugu project for Telangana) — that is how language routing is configured.
 */

export const PAYMENT_MODE_OPTIONS = [
    { value: 'COD', label: 'COD' },
    { value: 'PREPAID', label: 'Prepaid' },
];

// The 5 zones actually stamped on order merchant pricing (ServicabilityZone.Zone) — the same
// list every other zone dropdown in the panel uses (FilterBar, Rules, CourierSLA). The API
// accepts any valid ServicabilityZone name, but only these occur on orders in practice.
export const ZONE_OPTIONS = [
    { value: 'LOCAL', label: 'Local' },
    { value: 'REGIONAL', label: 'Regional' },
    { value: 'METROS', label: 'Metro to Metro' },
    { value: 'ROI', label: 'Rest of India' },
    { value: 'SPECIAL', label: 'Special Zone' },
];

const GEO_ACTION_OPTIONS = [
    { value: 'CALL', label: 'Call' },
    { value: 'SKIP', label: "Don't call" },
];

// State vocabulary = distinct states in the pincode_master collection (the backend resolves an
// order's state from its drop pincode against the same collection, so the two always agree).
// Values are sent exactly as the master spells them; labels are title-cased for display.
const titleCase = (s) => String(s).replace(/\b([a-z])/g, (m) => m.toUpperCase());
let statesCache = null;
function useCallingFilterStates() {
    const [states, setStates] = React.useState(statesCache || []);
    React.useEffect(() => {
        if (statesCache) return;
        getGeneric(apiConstants.NDR_CALLING_STATES)
            .then((res) => {
                const list = Array.isArray(res?.data) ? res.data : [];
                statesCache = list.map((s) => ({ value: s, label: titleCase(s) }));
                setStates(statesCache);
            })
            .catch(() => setStates([]));
    }, []);
    return states;
}

const ATTEMPT_MIN_OPTIONS = [
    { value: '', label: 'Any attempt' },
    ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: n === 1 ? 'From 1st attempt' : `From attempt ${n}` })),
];
const ATTEMPT_MAX_OPTIONS = [
    { value: '', label: 'No upper limit' },
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ value: String(n), label: `Up to attempt ${n}` })),
];

export const CALLING_FILTER_FORM_DEFAULTS = {
    filter_enabled: false,
    filter_payment_modes: [],
    filter_min_value: '',
    filter_max_value: '',
    filter_zones: [],
    filter_allow_zone_missing: false,
    filter_min_attempt: '',
    filter_max_attempt: '',
    filter_courier_parents: [],
    filter_courier_account_codes: [],
    filter_min_address_score: '',
    filter_max_address_score: '',
    filter_min_rto_score: '',
    filter_max_rto_score: '',
    filter_allow_score_missing: false,
    filter_geo_rules: [],
    filter_geo_others: 'CALL',
};

const numOrEmpty = (v) => (v != null ? String(v) : '');
const newGeoRow = () => ({ states: [], pincodes: '', action: 'CALL', tele_project_id: '' });

/** Does a saved calling_filter carry at least one criterion? */
export function callingFilterHasCriteria(cf) {
    if (!cf) return false;
    return (cf.payment_modes || []).length > 0 ||
        cf.min_shipment_value != null || cf.max_shipment_value != null ||
        (cf.zones || []).length > 0 ||
        cf.min_attempt_count != null || cf.max_attempt_count != null ||
        (cf.courier_parents || []).length > 0 || (cf.courier_account_codes || []).length > 0 ||
        cf.min_address_score != null || cf.max_address_score != null ||
        cf.min_rto_score != null || cf.max_rto_score != null ||
        (cf.geo_rules || []).length > 0;
}

/** Pure: saved calling_filter -> flat form values (keys of CALLING_FILTER_FORM_DEFAULTS). */
export function callingFilterToFormValues(cf) {
    return {
        filter_enabled: callingFilterHasCriteria(cf),
        filter_payment_modes: cf?.payment_modes || [],
        filter_min_value: numOrEmpty(cf?.min_shipment_value),
        filter_max_value: numOrEmpty(cf?.max_shipment_value),
        filter_zones: cf?.zones || [],
        filter_allow_zone_missing: !!cf?.allow_if_zone_missing,
        filter_min_attempt: numOrEmpty(cf?.min_attempt_count),
        filter_max_attempt: numOrEmpty(cf?.max_attempt_count),
        filter_courier_parents: cf?.courier_parents || [],
        filter_courier_account_codes: cf?.courier_account_codes || [],
        filter_min_address_score: numOrEmpty(cf?.min_address_score),
        filter_max_address_score: numOrEmpty(cf?.max_address_score),
        filter_min_rto_score: numOrEmpty(cf?.min_rto_score),
        filter_max_rto_score: numOrEmpty(cf?.max_rto_score),
        filter_allow_score_missing: !!cf?.allow_if_score_missing,
        filter_geo_rules: (cf?.geo_rules || []).map((g) => ({
            states: g.states || [],
            pincodes: (g.pincodes || []).join(', '),
            action: g.action || 'CALL',
            tele_project_id: g.tele_project_id || '',
        })),
        filter_geo_others: cf?.geo_others || 'CALL',
    };
}

/** Prefill the filter form fields from a saved partner config's calling_filter. */
export function applyCallingFilterToForm(setValue, callingFilter) {
    const values = callingFilterToFormValues(callingFilter);
    Object.keys(values).forEach((k) => setValue(k, values[k]));
}

/**
 * Build the calling_filter payload from form values.
 * Returns { error } on invalid input, otherwise { callingFilter } where callingFilter is:
 *  - an object with the filter criteria when the filter is enabled,
 *  - {} when the filter was just disabled and one existed before (explicit clear),
 *  - undefined when disabled and none existed (omit from payload — leave backend untouched).
 */
export function buildCallingFilterPayload(data, hadFilter) {
    if (!data.filter_enabled) {
        return hadFilter ? { callingFilter: {} } : { callingFilter: undefined };
    }

    // RHFTextField type="number" coerces a cleared field to 0 (+'' === 0), so 0 means "unset".
    // A 0 bound is never meaningful anyway (min 0 = no-op, max 0 would block everything).
    const toNumber = (v) => {
        if (v === '' || v === null || v === undefined) return null;
        const n = Number(v);
        return n === 0 ? null : n;
    };
    const toScore = (v, label) => {
        const n = toNumber(v);
        if (n === null) return { n: null };
        if (isNaN(n) || n < 0 || n > 100) return { error: `${label} must be between 0 and 100` };
        return { n };
    };

    const min = toNumber(data.filter_min_value);
    const max = toNumber(data.filter_max_value);
    if ((min !== null && (isNaN(min) || min < 0)) || (max !== null && (isNaN(max) || max < 0))) {
        return { error: 'Shipment value bounds must be numbers >= 0' };
    }
    if (min !== null && max !== null && min > max) {
        return { error: 'Min shipment value must be <= max shipment value' };
    }

    const callingFilter = {};
    if ((data.filter_payment_modes || []).length > 0) {
        callingFilter.payment_modes = data.filter_payment_modes;
    }
    if (min !== null) callingFilter.min_shipment_value = min;
    if (max !== null) callingFilter.max_shipment_value = max;
    if ((data.filter_zones || []).length > 0) {
        callingFilter.zones = data.filter_zones;
        callingFilter.allow_if_zone_missing = !!data.filter_allow_zone_missing;
    }

    const minAttempt = toNumber(data.filter_min_attempt);
    const maxAttempt = toNumber(data.filter_max_attempt);
    if (minAttempt !== null && maxAttempt !== null && minAttempt > maxAttempt) {
        return { error: 'Attempt "from" must be <= attempt "up to"' };
    }
    if (minAttempt !== null) callingFilter.min_attempt_count = minAttempt;
    if (maxAttempt !== null) callingFilter.max_attempt_count = maxAttempt;

    if ((data.filter_courier_parents || []).length > 0) {
        callingFilter.courier_parents = data.filter_courier_parents;
    }
    if ((data.filter_courier_account_codes || []).length > 0) {
        callingFilter.courier_account_codes = data.filter_courier_account_codes;
    }

    const minAddr = toScore(data.filter_min_address_score, 'Min address score');
    if (minAddr.error) return { error: minAddr.error };
    const maxAddr = toScore(data.filter_max_address_score, 'Max address score');
    if (maxAddr.error) return { error: maxAddr.error };
    if (minAddr.n !== null && maxAddr.n !== null && minAddr.n > maxAddr.n) {
        return { error: 'Min address score must be <= max address score' };
    }
    if (minAddr.n !== null) callingFilter.min_address_score = minAddr.n;
    if (maxAddr.n !== null) callingFilter.max_address_score = maxAddr.n;

    const minRto = toScore(data.filter_min_rto_score, 'Min RTO score');
    if (minRto.error) return { error: minRto.error };
    const maxRto = toScore(data.filter_max_rto_score, 'Max RTO score');
    if (maxRto.error) return { error: maxRto.error };
    if (minRto.n !== null && maxRto.n !== null && minRto.n > maxRto.n) {
        return { error: 'Min RTO score must be <= max RTO score' };
    }
    if (minRto.n !== null) callingFilter.min_rto_score = minRto.n;
    if (maxRto.n !== null) callingFilter.max_rto_score = maxRto.n;
    if (minAddr.n !== null || maxAddr.n !== null || minRto.n !== null || maxRto.n !== null) {
        callingFilter.allow_if_score_missing = !!data.filter_allow_score_missing;
    }

    const geoRows = data.filter_geo_rules || [];
    if (geoRows.length > 0) {
        const geo_rules = [];
        for (let i = 0; i < geoRows.length; i += 1) {
            const row = geoRows[i];
            const pincodes = String(row.pincodes || '')
                .split(/[\s,;]+/)
                .map((p) => p.trim())
                .filter((p) => p.length > 0);
            const badPin = pincodes.find((p) => !/^\d{6}$/.test(p));
            if (badPin) return { error: `State/Pincode row ${i + 1}: invalid pincode "${badPin}" (6 digits expected)` };
            const states = row.states || [];
            if (states.length === 0 && pincodes.length === 0) {
                return { error: `State/Pincode row ${i + 1}: pick at least one state or pincode (or remove the row)` };
            }
            const rule = { action: row.action || 'CALL' };
            if (states.length > 0) rule.states = states;
            if (pincodes.length > 0) rule.pincodes = Array.from(new Set(pincodes));
            if (rule.action === 'CALL' && row.tele_project_id && String(row.tele_project_id).trim()) {
                rule.tele_project_id = String(row.tele_project_id).trim();
            }
            geo_rules.push(rule);
        }
        callingFilter.geo_rules = geo_rules;
        callingFilter.geo_others = data.filter_geo_others || 'CALL';
    }

    return { callingFilter };
}

/** Compact one-line description of a saved calling_filter for the config grid. */
export function summarizeCallingFilter(filter) {
    if (!filter) return 'All shipments';
    const parts = [];
    if ((filter.payment_modes || []).length > 0) parts.push(filter.payment_modes.join('/'));
    const min = filter.min_shipment_value;
    const max = filter.max_shipment_value;
    if (min != null && max != null) parts.push(`₹${min}–₹${max}`);
    else if (min != null) parts.push(`≥ ₹${min}`);
    else if (max != null) parts.push(`≤ ₹${max}`);
    if ((filter.zones || []).length > 0) {
        parts.push(`Zones: ${filter.zones.join(', ')}${filter.allow_if_zone_missing ? ' (+unzoned)' : ''}`);
    }
    const minA = filter.min_attempt_count;
    const maxA = filter.max_attempt_count;
    if (minA != null && maxA != null) parts.push(`Attempts ${minA}–${maxA}`);
    else if (minA != null) parts.push(`From attempt ${minA}`);
    else if (maxA != null) parts.push(`Up to attempt ${maxA}`);
    const courierBits = [
        ...(filter.courier_parents || []),
        ...((filter.courier_account_codes || []).length > 0 ? [`${filter.courier_account_codes.length} account code(s)`] : []),
    ];
    if (courierBits.length > 0) parts.push(`Couriers: ${courierBits.join(', ')}`);
    const range = (label, lo, hi) => {
        if (lo == null && hi == null) return;
        parts.push(lo != null && hi != null ? `${label} ${lo}–${hi}` : lo != null ? `${label} ≥ ${lo}` : `${label} ≤ ${hi}`);
    };
    range('Address score', filter.min_address_score, filter.max_address_score);
    range('RTO score', filter.min_rto_score, filter.max_rto_score);
    if (filter.allow_if_score_missing && (filter.min_address_score != null || filter.max_address_score != null
        || filter.min_rto_score != null || filter.max_rto_score != null)) parts.push('(+unscored)');
    (filter.geo_rules || []).forEach((g) => {
        const where = [...(g.states || []).map(titleCase), ...((g.pincodes || []).length ? [`${g.pincodes.length} pincode(s)`] : [])].join(', ');
        parts.push(`${where}: ${g.action === 'SKIP' ? "don't call" : g.tele_project_id ? `call in ${g.tele_project_id}` : 'call'}`);
    });
    if ((filter.geo_rules || []).length > 0) parts.push(`other regions: ${filter.geo_others === 'SKIP' ? "don't call" : 'call'}`);
    return parts.length > 0 ? parts.join(' · ') : 'All shipments';
}

/** Grid column showing the saved filter on the partner config row. */
export const callingFilterColumn = {
    field: 'calling_filter',
    headerName: 'Calling Filter',
    width: 260,
    flex: 1.3,
    renderCell: (col) => (
        <Typography variant="caption" sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>
            {summarizeCallingFilter(col?.row?.calling_filter)}
        </Typography>
    ),
};

/**
 * Couriers: whole parents and/or specific account codes — the same Parent -> Account Code chaining
 * used by FilterBar. Account-code options are scoped to the selected parents when any are picked
 * (otherwise every code, searchable). A shipment matches if its parent OR its account code is listed.
 */
function CourierFields() {
    const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);
    const watchedParents = useWatch({ name: 'filter_courier_parents' });
    const selectedParents = React.useMemo(() => (Array.isArray(watchedParents) ? watchedParents : []), [watchedParents]);
    const list = React.useMemo(() => (Array.isArray(couriers) ? couriers : []), [couriers]);

    const parentOptions = React.useMemo(() => {
        const seen = new Map();
        list.forEach((c) => {
            if (c?.parent && !seen.has(c.parent.toLowerCase())) seen.set(c.parent.toLowerCase(), c.parent);
        });
        return Array.from(seen.values()).sort().map((p) => ({ value: p, label: p }));
    }, [list]);

    const codeOptions = React.useMemo(() => {
        const wanted = new Set(selectedParents.map((p) => String(p).toLowerCase()));
        return list
            .filter((c) => c?.cpAccountCode && (wanted.size === 0 || (c.parent && wanted.has(c.parent.toLowerCase()))))
            .map((c) => ({ value: c.cpAccountCode, label: `${c.cpAccountCode} (${c.parent || '-'})` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [list, selectedParents]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
                <RHFMultipleSelectField width="100%" search name="filter_courier_parents"
                    label="Courier Parents (whole parent)" options={parentOptions} />
            </Grid>
            <Grid item xs={12} md={7}>
                <RHFMultipleSelectField width="100%" search name="filter_courier_account_codes"
                    label={selectedParents.length > 0 ? 'Specific Account Codes (under selected parents)' : 'Specific Account Codes'}
                    options={codeOptions} />
            </Grid>
            <Grid item xs={12} sx={{ pt: '0 !important' }}>
                <Typography variant="caption" color="text.secondary">
                    A shipment is called if its courier parent is listed OR its account code is listed. Leave both empty to not restrict by courier.
                </Typography>
            </Grid>
        </Grid>
    );
}

/**
 * State / Pincode rows. Each row: states and/or pincodes -> Call (optionally in a specific project,
 * for language) or Don't call. Shipments matching no row follow "All other regions".
 */
function GeoRuleFields({ supportsProject }) {
    const { control } = useFormContext();
    const { fields, append, remove } = useFieldArray({ control, name: 'filter_geo_rules' });
    const rows = useWatch({ name: 'filter_geo_rules' }) || [];
    const stateOptions = useCallingFilterStates();

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">
                State / Pincode — rows are checked top to bottom; the first row containing the shipment's delivery state or pincode decides.
                {supportsProject ? ' Set a Project ID on a row to call those regions in a different (e.g. regional-language) project.' : ''}
            </Typography>
            {fields.map((f, i) => (
                <Grid container spacing={1} alignItems="center" key={f.id} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={4}>
                        <RHFMultipleSelectField width="100%" search name={`filter_geo_rules.${i}.states`} label="States" options={stateOptions} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFTextField name={`filter_geo_rules.${i}.pincodes`} label="Pincodes (comma separated)" color="secondary"
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={5} md={2}>
                        <RHFSelectField width="100%" name={`filter_geo_rules.${i}.action`} label="Action" options={GEO_ACTION_OPTIONS} />
                    </Grid>
                    <Grid item xs={5} md={2}>
                        {supportsProject && (rows[i]?.action || 'CALL') === 'CALL' && (
                            <RHFTextField name={`filter_geo_rules.${i}.tele_project_id`} label="Project ID (optional)" color="secondary"
                                InputLabelProps={{ shrink: true }} helperText="Empty = default project" />
                        )}
                    </Grid>
                    <Grid item xs={2} md={1}>
                        <IconButton size="small" color="error" onClick={() => remove(i)} aria-label="remove row">
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
            ))}
            <Grid container spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={5}>
                    <Button size="small" variant="text" onClick={() => append(newGeoRow())}>+ Add state / pincode row</Button>
                </Grid>
                {fields.length > 0 && (
                    <Grid item xs={12} md={4}>
                        <RHFSelectField width="100%" name="filter_geo_others" label="All other regions" options={GEO_ACTION_OPTIONS} />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}

/**
 * Form fields for the calling filter. Must be rendered inside a FormProvider whose form
 * includes CALLING_FILTER_FORM_DEFAULTS.
 *
 * @param netraEnabled    merchant has "Use Netra" on; when false the score fields are shown disabled
 *                        with a hint, since the backend rejects score criteria for non-Netra merchants
 * @param supportsProject partner has per-lead projects (Futwork, GrowupNext) -> geo rows can route
 */
export default function CallingFilterFields({ netraEnabled = true, supportsProject = true }) {
    const filterEnabled = useWatch({ name: 'filter_enabled' });
    const zones = useWatch({ name: 'filter_zones' }) || [];

    return (
        <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <RHFSwitch
                name="filter_enabled"
                label="Calling Filter (push only matching shipments)"
                helperText="When off, every eligible NDR shipment is pushed for calling. When on, a shipment is called only if it matches ALL the conditions set below — leave a field empty to not restrict by it."
            />
            {filterEnabled && (
                <Grid container spacing={2} alignItems="flex-start" sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={4}>
                        <RHFMultipleSelectField width="100%" name="filter_payment_modes" label="Payment Modes" options={PAYMENT_MODE_OPTIONS} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_min_value" label="Min Value (₹)" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="Inclusive (≥)" />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_max_value" label="Max Value (₹)" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="Inclusive (≤)" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <RHFMultipleSelectField width="100%" name="filter_zones" label="Zones" options={ZONE_OPTIONS} />
                    </Grid>
                    {zones.length > 0 && (
                        <Grid item xs={12}>
                            <RHFSwitch
                                name="filter_allow_zone_missing"
                                label="Also call shipments without a priced zone"
                                helperText="Applies only when zones are selected. Off = unpriced shipments are skipped (cost-conservative)."
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={3}>
                        <RHFSelectField width="100%" name="filter_min_attempt" label="Call from attempt" options={ATTEMPT_MIN_OPTIONS} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <RHFSelectField width="100%" name="filter_max_attempt" label="Call up to attempt" options={ATTEMPT_MAX_OPTIONS} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                            Attempt = delivery attempts so far (OFD count incl. the one that just failed). "From attempt 2" skips calling on the first NDR.
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <CourierFields />
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_min_address_score" label="Min Address Score" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="0–100 (Netra)" disabled={!netraEnabled} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_max_address_score" label="Max Address Score" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="0–100 (Netra)" disabled={!netraEnabled} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_min_rto_score" label="Min RTO Score" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="0–100, higher = riskier" disabled={!netraEnabled} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <RHFTextField name="filter_max_rto_score" label="Max RTO Score" type="number" color="secondary"
                            InputLabelProps={{ shrink: true }} helperText="0–100, higher = riskier" disabled={!netraEnabled} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {netraEnabled ? (
                            <RHFSwitch name="filter_allow_score_missing" label="Also call shipments with no Netra score"
                                helperText="Orders created before Netra was enabled carry no score. Off = they are skipped." />
                        ) : (
                            <Typography variant="caption" color="warning.main">
                                Netra scoring is not enabled for this merchant. Turn on "Use Netra" on the merchant first to filter by address / RTO score.
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <GeoRuleFields supportsProject={supportsProject} />
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
